import  { useEffect, useState } from 'react';
import { getWeeklyPlanningForecasts, listFormations, listPlanning, regenerateWeeklyShiftsForecasts } from '../api/restaurant';
import PlanningTable from '../components/Tables/PlanningTable';
import WeeklyPlanning from '../types/api/WeeklyPlanning';
import { Button, IconButton, Paper, Stack, Typography, useTheme } from '@mui/material';
import { getDateMonthNextSunday, getDateMonthPreviousMonday, getWeekNumber, getYearOfDate } from '../tools/utils';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Formation from '../types/api/Formation';
import { DailyClientForecast } from '../types/api/DailyClientForecast';
import { WeeklyShiftForecast } from '../types/api/WeeklyShiftForecast';
import WeeklyShiftsForecastTable from '../components/Tables/WeeklyShiftForecastTable';
import { RegenerateForecastOption } from '../types/api/RegenerateForecastOption';

interface PlanningProps {}

export default function Planning(_props: PlanningProps) {
  const theme = useTheme();

  const [mode, setMode] = useState<'planning' | 'weeklyShifts'>('planning');

  const [weeklyPlannings, setWeeklyPlannings] = useState({id: -1, weekNumber: -1, year: -1, EmployeeWorkWeeks: [], unassignedShifts: [], forecasts: [], dailyNeeds: []} as WeeklyPlanning)
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [formations, setFormations] = useState<Formation[]>([]);
  const [currentWeeklyShiftsForecasts, setCurrentWeeklyShiftsForecasts] = useState<WeeklyShiftForecast[]>([]);
  const [currentDailyClientForecasts, setCurrentDailyClientForecasts] = useState<DailyClientForecast[]>([]);


  const updateFormations = async () => {
    const res = await listFormations();
    if (res && res.code === 200 && res.data) {
      setFormations(res.data);
    }
  };

  const refreshPlanning = async () => {
    setIsLoading(true);
    try {
      const res = await listPlanning(getWeekNumber(selectedWeek), selectedWeek.getFullYear());
      if (res && res.data && res.code === 200) {
        setWeeklyPlannings(res.data);
      }
    } catch (e) {
      // keep simple: swallow and stop loading; errors are visible in console/network
      console.error('refreshPlanning error', e);
    } finally {
      setIsLoading(false);
    }
  }

  const refreshWeeklyShiftsForecasts = async () => {
      setIsLoading(true);
      try {
          const res = await getWeeklyPlanningForecasts(selectedWeek.getFullYear(), getWeekNumber(selectedWeek));
          if (res && res.data && res.code === 200) {
              setCurrentWeeklyShiftsForecasts(res.data.forecasts);
              setCurrentDailyClientForecasts(res.data.clientForecasts);
          }
      } catch (e) {
          console.error('refreshPlanning error', e);
      } finally {
          setIsLoading(false);
      }
  }

  const _regenerateWeeklyShiftsForecasts = async (regenerateForecastOptions: RegenerateForecastOption) => {
      try {
          const res = await regenerateWeeklyShiftsForecasts(selectedWeek.getFullYear(), getWeekNumber(selectedWeek), regenerateForecastOptions);
          if (res && res.data && res.code === 200) {
              setCurrentWeeklyShiftsForecasts(res.data);
          }
      } catch (e) {
          console.error('refreshPlanning error', e);
      }
  }


  useEffect(() => {
    // load planning when selectedWeek changes (and on mount)
    refreshPlanning();
    refreshWeeklyShiftsForecasts();
  }, [selectedWeek]);

  useEffect(() => {
      updateFormations();                
  }, []);



  const renderTable = () => {
    if (mode === 'planning') {
      return (
        <PlanningTable
          selectedWeek={selectedWeek}
          setSelectedWeek={setSelectedWeek}
          plannings={weeklyPlannings}
          setPlanning={setWeeklyPlannings}
          refreshPlanning={refreshPlanning}
        />
      );
    } else {
      return (
        <WeeklyShiftsForecastTable
          shifts={currentWeeklyShiftsForecasts} 
          clientForecasts={currentDailyClientForecasts}
          selectedWeek={selectedWeek}
          formations={formations}
          refreshWeeklyShiftsForecasts={refreshWeeklyShiftsForecasts}
          regenerateWeeklyShiftsForecasts={_regenerateWeeklyShiftsForecasts}
        />
      )
    }
  }

  return (
    <div>
      {/* Header */}
      <Paper elevation={0} variant="outlined" sx={{
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: 2,
          borderRadius: 2,
          marginBottom: 2
      }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton onClick={()=>{
            const newDate = new Date(selectedWeek.getTime())
            newDate.setDate(newDate.getDate()-7)
            setSelectedWeek(newDate)
          }}
            sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, height: 40, width: 40 }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="h6" 
            sx={{ 
              fontWeight: 600, 
              border: `1px solid ${theme.palette.divider}`, 
              borderRadius: 1, 
              padding: '0 16px',
              height: 40,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            Semaine {getWeekNumber(selectedWeek)} • {getDateMonthPreviousMonday(selectedWeek)} au {getDateMonthNextSunday(selectedWeek)} {getYearOfDate(selectedWeek)}
          </Typography>
          <IconButton onClick={()=>{
              const newDate = new Date(selectedWeek.getTime())
              newDate.setDate(newDate.getDate()+7)
              setSelectedWeek(newDate)
            }}
            sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, height: 40, width: 40 }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>

          <Stack
            direction="row"
            alignItems="center"
            sx={{ marginLeft: 2, borderColor: theme.palette.divider }}
          >
            <Button
              variant="contained"
              color={mode === "planning" ? "primary" : "inherit"}
              onClick={()=>{
                setMode("planning")
              }}
              sx={{borderTopRightRadius: 0, borderBottomRightRadius: 0}}
            >
              PLANNING
            </Button>
            <Button
              variant="contained"
              color={mode === "weeklyShifts" ? "primary" : "inherit"}
              onClick={()=>{
                setMode("weeklyShifts")
              }}
              sx={{borderTopLeftRadius: 0, borderBottomLeftRadius: 0}}
            >
              PRÉVISIONNELS
            </Button>
          </Stack>

        </Stack>
      </Paper>

      <Paper
        elevation={0}
        variant="outlined"
      >
        {renderTable()}
      </Paper>
    </div>
  )
}